import React, { useState } from "react";
import { Download, Copy, Magnet as MagnetIcon, ArrowUp, ArrowDown, Info, Flag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import TorrentInfoModal from "@/components/TorrentInfoModal";
import ModRowControls from "@/components/ModRowControls";
import ReportTorrentModal from "@/components/ReportTorrentModal";
import { QualityBadge, ResolutionBadge, CodecBadge, LanguageBadge, HdrBadge, SourceBadge, ReleaseSourceBadge, AudioBadges } from "@/components/TorrentBadges";
import OneTapInstall from "@/components/OneTapInstall";

export default function TorrentRow({ row, index, mediaType, tmdbId, onModChange }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const copyMagnet = async () => {
    if (!row.magnet) {
      toast.error("Magnet link unavailable for this entry");
      return;
    }
    try {
      await navigator.clipboard.writeText(row.magnet);
      toast.success("Magnet link copied");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const open = () => {
    if (!row.magnet) {
      toast.error("Magnet link unavailable");
      return;
    }
    window.location.href = row.magnet;
  };

  return (
    <div
      data-testid={`torrent-list-row-${index}`}
      className={`relative flex flex-wrap items-center gap-3 py-4 px-4 md:px-6 border-b border-[var(--tt-border)] hover:bg-white/[0.03] transition-colors ${
        row._recommended
          ? "bg-emerald-400/[0.07] ring-1 ring-inset ring-emerald-400/40"
          : ""
      }`}
    >
      {row._recommended && (
        <span
          data-testid={`torrent-list-row-${index}-recommended-ribbon`}
          title={
            row._recommendedReasons?.length
              ? `Best fit for ${row._recommendedFor}: ${row._recommendedReasons.join(", ")}`
              : `Best fit for ${row._recommendedFor}`
          }
          className="absolute -top-2 left-3 md:left-5 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400 text-black text-[9px] font-display font-extrabold uppercase tracking-wider shadow-[0_0_10px_-2px_rgba(52,211,153,0.7)]"
        >
          <Sparkles className="w-2.5 h-2.5" />
          Best for your {row._recommendedFor}
        </span>
      )}
      <SourceBadge source={row.source} />
      {row.resolution
        ? <ResolutionBadge resolution={row.resolution} />
        : <QualityBadge quality={row.quality} />}
      <HdrBadge hdr={row.hdr} />
      <ReleaseSourceBadge source={row.release_source} />
      <CodecBadge codec={row.codec} />
      <AudioBadges audio={row.audio} />
      <LanguageBadge language={row.language} />
      <button
        type="button"
        onClick={() => setInfoOpen(true)}
        data-testid={`torrent-name-${index}`}
        className="text-sm flex-1 min-w-[200px] truncate text-left hover:text-[var(--tt-primary)] transition-colors"
        title={`${row.name} — click for file list & trackers`}
      >
        {row.name}
      </button>
      <span className="text-xs font-mono-tt text-[var(--tt-text-muted)] min-w-[80px] text-right">
        {row.size}
      </span>
      <span className="text-xs flex items-center gap-1 text-emerald-400 font-mono-tt min-w-[50px]">
        <ArrowUp className="w-3 h-3" />
        {row.seeders}
      </span>
      <span className="text-xs flex items-center gap-1 text-red-400 font-mono-tt min-w-[50px]">
        <ArrowDown className="w-3 h-3" />
        {row.leechers}
      </span>
      <button
        onClick={() => setInfoOpen(true)}
        data-testid={`torrent-info-btn-${index}`}
        className="p-2 rounded-md hover:bg-white/10 transition-colors text-[var(--tt-text-muted)] hover:text-[var(--tt-purple)]"
        title="Files & trackers"
      >
        <Info className="w-4 h-4" />
      </button>
      <button
        onClick={() => setReportOpen(true)}
        disabled={!row.info_hash}
        data-testid={`torrent-report-btn-${index}`}
        className="p-2 rounded-md hover:bg-white/10 transition-colors text-[var(--tt-text-muted)] hover:text-[var(--tt-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
        title="Report broken / malicious torrent"
      >
        <Flag className="w-4 h-4" />
      </button>
      <button
        onClick={copyMagnet}
        data-testid="torrent-magnet-btn"
        className="p-2 rounded-md hover:bg-white/10 transition-colors text-[var(--tt-text-muted)] hover:text-white"
        title="Copy magnet"
      >
        <Copy className="w-4 h-4" />
      </button>
      <button
        onClick={open}
        data-testid="torrent-download-btn"
        className="flex items-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
      >
        <MagnetIcon className="w-3.5 h-3.5" />
        Magnet
      </button>
      {row._recommended && (
        <OneTapInstall
          row={row}
          recommendedFor={row._recommendedFor}
          tmdbId={tmdbId}
          mediaType={mediaType}
        />
      )}

      <ModRowControls row={row} mediaType={mediaType} onChange={onModChange} />

      {infoOpen && (
        <TorrentInfoModal row={row} mediaType={mediaType} tmdbId={tmdbId} onClose={() => setInfoOpen(false)} />
      )}
      {reportOpen && (
        <ReportTorrentModal
          row={row}
          tmdbId={tmdbId}
          mediaType={mediaType}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}
